import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIGS
import { AppIntegrationAccountApiService } from '../../../shared';
import {
  EAppIntegrationAccountCompanyFilter,
  IAppIntegrationAccount,
  IAppIntegrationAccountCompany,
} from '../../../shared/interfaces/app-integration-account.interface';
import { trackByControlValue, trackById } from '../../../shared/util/track-by.util';
import { EAppAccountType } from '../../../system-setting/constants';

import { DialogConfig } from './../../../shared/configs/dialog.config';
import { PaginationConfig } from './../../../shared/configs/paginator.config';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';

// ENUMS

// INTERFACES

import { IPagination } from './../../../shared/interfaces/paginator.interface';

// SERVICES
import { AppIntegrationAccountCompaniesApiService } from './../../../shared/services/api/app-integration/app-integration-account-companies-api.service';
import { ClientCompanyApiService } from './../../../shared/services/api/client/client-company-api.service';

// UTILS

@Component({
  selector: 'lum-app-integration-account-manage-companies',
  templateUrl: './app-integration-account-manage-companies.component.html',
  styleUrls: ['./app-integration-account-manage-companies.component.scss'],
})
export class AppIntegrationAccountManageCompaniesComponent
  implements OnInit, OnDestroy
{
  constructor(
    private dialog :MatDialog,
    private appIntegrationAccountApiService :AppIntegrationAccountApiService,
    private appIntegrationAccountCompaniesApiService :AppIntegrationAccountCompaniesApiService,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private clientCompanyApiService :ClientCompanyApiService,
    private route :ActivatedRoute,
    private router :Router,
    private snackBar :MatSnackBar,
  ) {}

  companies :IAppIntegrationAccountCompany[] = [];
  appIntegrationAccount :IAppIntegrationAccount;
  loadingCompanies :boolean = false;
  autoMapLoading :boolean = false;
  filteredCompanies :any[] = [];
  loadingSearchCompanies :boolean = false;
  form :UntypedFormGroup;
  accountId :number;
  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    path: '',
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };
  queryParams :{ [param :string] :string | string[] };
  eAppIntegrationAccountCompanyFilter = EAppIntegrationAccountCompanyFilter;

  private onDestroy$ = new Subject<void>();

  trackByControlValue = trackByControlValue;
  trackById = trackById;

  ngOnInit() :void {
    this.accountId = Number(this.route?.parent?.snapshot.paramMap.get('id'));

    if (!this.accountId) {
      this.router.navigate(['settings/app-integration-account-settings']);

      return;
    }

    this.initQueryParams();
    this.initForm([]);
    this.getAppIntegrationAccount();
    this.getCompanies();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSearchCompanies(companyName :string) :void {
    this.loadingSearchCompanies = true;
    this.clientCompanyApiService
      .getV2({
        business_name: companyName,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingSearchCompanies = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response?.records?.data) {
            this.filteredCompanies = response.records.data;
            if (response.records.data.length === 0) {
              this.snackBar.open('No Company found', 'Info', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            }
          } else {
            this.snackBar.open('Error: Invalid response format', 'Error', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to search Companies',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  onSelectCompany(event :MatAutocompleteSelectedEvent, index :number) :void {
    const lumCompanyId = event.option.value.id;
    const data = {
      lumCompanyId,
    };

    const companyId = this.companiesFormArray.at(index).get('id')?.value;

    this.updateCompany({ data, index, companyId });
  }

  onClearCompany(index :number) :void {
    this.companiesFormArray.at(index).get('lumCompanyName')?.setValue('');
  }

  displayFn(company :any) :string {
    // Check if rfq type is string
    if (typeof company === 'string') {
      return company;
    }

    return `${company?.business_name}`;
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };

    this.getCompanies();
  }

  onFilterChange(value :string, key :string) :void {
    this.queryParams = {
      ...this.queryParams,
      [key]: value,
    };

    this.getCompanies();
  }

  onSyncCompanies() :void {
    this.loadingCompanies = true;
    this.appIntegrationAccountCompaniesApiService
      .syncCompanies({ appIntegrationAccountId: this.accountId.toString() })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingCompanies = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { data, meta } = response;
          this.companies = data;
          this.pagination = meta;
          if (data.length) {
            this.initForm(data);
          }

          this.snackBar.open(
            'Sync in progress - this may take a while. Please refresh the page after a few minutes.',
            'Success',
            { duration: SnackBarConfig.DEFAULT.duration },
          );
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to sync companies', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onAutoMapCompanies() :void {
    this.autoMapLoading = true;

    this.appIntegrationAccountCompaniesApiService
      .autoMapCompanies(this.accountId)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.autoMapLoading = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :IAppIntegrationAccountCompany[]) => {
          if (response.length > 0) {
            this.getCompanies();
            this.snackBar.open(
              'Company have been mapped successfully.',
              'Success',
              {
                duration: SnackBarConfig.DEFAULT.duration,
              },
            );
          } else {
            this.snackBar.open('No unmapped companies found.', 'Info', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to auto map companies',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private updateCompany(params :{
    data :any;
    index :number;
    companyId :number;
  }) :void {
    const { data, companyId, index } = params;
    this.appIntegrationAccountCompaniesApiService
      .update(companyId, data)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open('Successfully updated company', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to update company', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      relations: 'lumCompany',
      mapTo: EAppIntegrationAccountCompanyFilter.ALL,
      synchronous: 'false',
    };
  }

  private getCompanies() :void {
    this.loadingCompanies = true;
    this.appIntegrationAccountCompaniesApiService
      .get({
        ...this.queryParams,
        appIntegrationAccountId: this.accountId.toString(),
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingCompanies = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          const { data, meta } = response;
          this.companies = data;
          this.pagination = meta;
          if (data.length) {
            this.initForm(data);
          }
        },
        error: (error) => {
          this.snackBar.open(error.message || 'Failed to get companies', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getAppIntegrationAccount() :void {
    this.appIntegrationAccountApiService
      .getById(this.accountId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (appIntegrationAccount :IAppIntegrationAccount) => {
          this.appIntegrationAccount =
            appIntegrationAccount || ({} as IAppIntegrationAccount);
        },
        error: (error) => {
          this.snackBar.open(
            error.message || 'Failed to get app integration account',
            'Fail',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  private initForm(companies :IAppIntegrationAccountCompany[]) :void {
    this.form = this.fb.group({
      companies: this.fb.array(
        companies.map((company) => {
          return this.fb.group({
            id: [company.id],
            lumCompanyId: [company.lumCompanyId],
            lumCompanyName: [
              company.lumCompany ? `${company.lumCompany?.businessName}` : '',
            ],
            companyId: [company.companyId],
            appIntegrationAccountId: [company.appIntegrationAccountId],
            name: [company.name],
            updatedAt: [company.updatedAt || null],
          });
        }),
      ),
    });
  }

  get companiesFormArray() :UntypedFormArray {
    return this.form.get('companies') as UntypedFormArray;
  }
}
